/**
 * Las cuentas de banco en las pantallas (`bancos.ts` in shared, 2026-10-10): which the shop has,
 * what each is called, which one a payment starts on, and what each holds by the books.
 *
 * Every record that moves money by transfer names its account, whatever the screen asked: a shop
 * with one account in a currency is never asked which (`ElegirCuenta`), and its records name that
 * one, so the books say where the money is even after a second account is opened.
 */

import Dexie from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { ACCOUNTS } from '@abastece/core';
import {
  centavos,
  cuentasDeBanco,
  esCuentaEnDolares,
  fxRate,
  usdToCordobas,
  type Centavos,
  DEFAULT_SETTINGS,
  type CuentaBanco,
  type CuentaBancoConfig,
  type CuentaBancoCordobas,
  type CuentaBancoDolares,
  type CuentaDeBanco,
  type Settings,
} from '@abastece/shared';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import type { Clave, Valores } from '../i18n/index.js';

type Traducir = (clave: Clave, valores?: Valores) => string;

/** The accounts the owner set up, for the screens that name accounts by the chart (`etiquetaCuenta`). */
export function useCuentasPuestas(): readonly CuentaBancoConfig[] {
  return useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS).cuentasBanco;
}

/** The shop's bank accounts, the closed ones too, in the chart's order. */
export const cuentasDeLaTienda = (config: Settings): CuentaDeBanco[] => cuentasDeBanco(config.cuentasBanco);

/** The ones a payment may name: open, and in `moneda` when it says one. */
export function cuentasAbiertas(config: Settings, moneda?: 'NIO' | 'USD'): CuentaDeBanco[] {
  return cuentasDeLaTienda(config).filter((c) => c.activa && (moneda === undefined || c.moneda === moneda));
}

/** What the owner named it, or null: the first of each currency is «Banco» or «Banco en dólares» until named. */
export const nombrePuesto = (config: Settings, cuenta: string): string | null =>
  config.cuentasBanco.find((c) => c.cuenta === cuenta)?.nombre ?? null;

/** Whether the owner has set up the shop's accounts: only then do the lists say which account each record went to. */
export const conCuentasPuestas = (config: Settings): boolean => config.cuentasBanco.length > 0;

/**
 * The accounts the money screens list, with what each holds (`saldosDeBancos`): the open córdoba
 * ones; the open dollar ones the owner set up, or that hold something; and a closed one only while
 * it still holds something. A shop that never set up its dollar account and never used it is not
 * shown one.
 */
export function cuentasEnUso(
  config: Settings,
  saldos: Readonly<Record<string, { cordobas: number; dolares: number }>>,
): CuentaDeBanco[] {
  return cuentasDeLaTienda(config).filter((c) => {
    const s = saldos[c.cuenta];
    const tiene = s !== undefined && (s.cordobas !== 0 || s.dolares !== 0);
    if (!c.activa) return tiene;
    return c.moneda === 'NIO' || nombrePuesto(config, c.cuenta) !== null || tiene;
  });
}

/** What the owner calls it; the first of each currency, until named, «Banco» or «Banco en dólares». */
export function nombreDeCuenta(config: Settings, cuenta: string, t: Traducir): string {
  const puesta = config.cuentasBanco.find((c) => c.cuenta === cuenta);
  if (puesta) return puesta.nombre;
  return t(esCuentaEnDolares(cuenta) ? 'efectivo.bancoDolares' : 'efectivo.banco');
}

/**
 * What a payroll payment came out of, by name: general cash, the wallet, or by transfer the bank
 * account it names (absent, «Bancos», as the books take it: `cuentaDeTransferencia`).
 */
export function deDondeSalio(config: Settings, formaPago: string | undefined, cuenta: string | null | undefined, t: Traducir): string {
  if (formaPago === 'transferencia') return nombreDeCuenta(config, cuenta ?? 'BANCOS', t);
  return t(formaPago === 'billetera' ? 'gastos.billeteraMovil' : 'efectivo.general');
}

/**
 * The córdoba account a payment by transfer starts on: the one transfers go to
 * (`cuentaTransferencias`, the owner, 2026-10-10: «The default should be the nicaraguan bank
 * account that the owner can change later»), or the first open one if that was closed since.
 */
export function cuentaDeTransferencias(config: Settings): CuentaBancoCordobas {
  return primeraAbierta(config, config.cuentaTransferencias);
}

/** The dollar account dollars go to unless another is picked: the first open one. */
export function primeraEnDolares(config: Settings): CuentaBancoDolares {
  return (cuentasAbiertas(config, 'USD')[0]?.cuenta ?? 'BANCO_DOLARES') as CuentaBancoDolares;
}

/** The córdoba account the card terminal pays into (`cuentaTarjeta`), the same way. */
export function cuentaDeLaTarjeta(config: Settings): CuentaBancoCordobas {
  return primeraAbierta(config, config.cuentaTarjeta);
}

function primeraAbierta(config: Settings, preferida: CuentaBancoCordobas): CuentaBancoCordobas {
  const abiertas = cuentasAbiertas(config, 'NIO');
  return (abiertas.find((c) => c.cuenta === preferida)?.cuenta ?? abiertas[0]?.cuenta ?? 'BANCOS') as CuentaBancoCordobas;
}

/**
 * The account a payment by transfer keeps: the one picked, while it still fits what is being paid,
 * or the one it starts on. A picked dollar account stops fitting when the amount goes back to
 * córdobas, say.
 */
export function cuentaQueQueda(config: Settings, elegida: CuentaBanco | null | undefined, moneda: 'NIO' | 'USD' = 'NIO'): CuentaBanco {
  const valen = cuentasAbiertas(config).filter((c) => c.moneda === 'NIO' || moneda === 'USD');
  return valen.some((c) => c.cuenta === elegida) ? elegida! : cuentaDeTransferencias(config);
}

/** The same for what only a córdoba account pays or takes: a customer's transfer, a refund, a wage. */
export function cuentaEnCordobas(config: Settings, elegida: CuentaBancoCordobas | null | undefined): CuentaBancoCordobas {
  return cuentaQueQueda(config, elegida, 'NIO') as CuentaBancoCordobas;
}

/**
 * The owner's money by transfer (`dueno.retiro`, `dueno.aporte`): the account it went to or came
 * from, and the amount as typed, which for a dollar account is its dollars, with what they come to
 * at the official rate as the córdobas the books carry them at.
 */
export function delDuenoPorBanco(
  config: Settings,
  cuenta: CuentaBanco,
  monto: Centavos,
): { monto: Centavos; cuenta: CuentaBanco; dolares?: Centavos } {
  return esCuentaEnDolares(cuenta) ? { monto: usdToCordobas(monto, fxRate(config.fxTasaOficial)), cuenta, dolares: monto } : { monto, cuenta };
}

/**
 * The owner's money in cash, as the books take it: dollars at the official rate, which is what
 * general cash carries them at, as «Dinero del dueño» on «Cuadrar la semana» sends them. Typed in
 * dollars and sent as córdobas, US$ 100 went in as C$ 100, and the next count of the safe found the
 * rest (2026-10-20).
 */
export function delDuenoEnEfectivo(config: Settings, forma: string, monto: Centavos): { monto: Centavos } {
  return forma === 'efectivo_dolares' ? { monto: usdToCordobas(monto, fxRate(config.fxTasaOficial)) } : { monto };
}

/** What each bank account holds by the books, its days added up: córdobas, and a dollar account's dollars. */
export async function saldosDeBancos(
  cuentas: readonly CuentaBanco[],
): Promise<Record<string, { cordobas: Centavos; dolares: Centavos }>> {
  const saldos: Record<string, { cordobas: Centavos; dolares: Centavos }> = {};
  for (const cuenta of cuentas) {
    let cordobas = 0;
    let dolares = 0;
    await db.saldosDiarios
      .where('[cuenta+fecha]')
      .between([ACCOUNTS[cuenta].code, Dexie.minKey], [ACCOUNTS[cuenta].code, Dexie.maxKey])
      .each((d) => {
        cordobas += d.debe - d.haber;
        dolares += d.dolares ?? 0;
      });
    saldos[cuenta] = { cordobas: centavos(cordobas), dolares: centavos(dolares) };
  }
  return saldos;
}
